import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { SiZendesk } from "react-icons/si";
import {
  Phone, Headset, CheckCircle, Plugs, CircleNotch, Brain, Ticket,
} from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const PROVIDERS = [
  {
    id: "freshdesk", name: "Freshdesk", category: "Helpdesk",
    desc: "Skicka inkommande Freshdesk-ärenden till dina AI-agenter för omedelbara förstasvar.",
    icon: <Ticket size={26} weight="bold" color="#25c16f" />,
    fields: [
      { key: "domain", label: "Freshdesk-domän", placeholder: "dittforetag.freshdesk.com" },
      { key: "api_key", label: "API-nyckel", placeholder: "Din Freshdesk API-nyckel", secret: true },
    ],
  },
  {
    id: "zendesk", name: "Zendesk", category: "Helpdesk",
    desc: "Anslut Zendesk för att automatiskt utkasta och lösa ärenden med dina AI-agenter.",
    icon: <SiZendesk size={26} color="#03363d" />,
    fields: [
      { key: "subdomain", label: "Zendesk-subdomän", placeholder: "dittforetag" },
      { key: "email", label: "Agentens e-post", placeholder: "agent@dittforetag.com" },
      { key: "api_token", label: "API-token", placeholder: "Din Zendesk API-token", secret: true },
    ],
  },
  {
    id: "zisson", name: "Zisson", category: "Telefoni",
    desc: "Anslut din Zisson-telefoniplattform för att lansera röstagenter på riktiga telefonlinjer.",
    icon: <Phone size={26} weight="bold" className="text-[#0033FF]" />,
    fields: [
      { key: "account_id", label: "Konto-ID", placeholder: "Zisson konto-ID" },
      { key: "api_key", label: "API-nyckel", placeholder: "Din Zisson API-nyckel", secret: true },
      { key: "sip_uri", label: "SIP-URI (valfritt)", placeholder: "sip:agent@zisson.com" },
    ],
  },
  {
    id: "genesys", name: "Genesys", category: "Telefoni",
    desc: "Driftsätt röstagenter på Genesys Cloud-infrastruktur för kontaktcenter.",
    icon: <Headset size={26} weight="bold" className="text-[#ff4f1f]" />,
    fields: [
      { key: "region", label: "Region", placeholder: "us-east-1" },
      { key: "client_id", label: "Klient-ID", placeholder: "Genesys OAuth klient-ID" },
      { key: "client_secret", label: "Klienthemlighet", placeholder: "Genesys klienthemlighet", secret: true },
    ],
  },
  {
    id: "openai", name: "Senzai AI-motor", category: "AI-modeller", connectedByDefault: true,
    desc: "Driver dina agenter med de senaste Senzai-modellerna. Hanteras av Senzum AI.",
    icon: <Brain size={26} weight="bold" className="text-[#10a37f]" />,
    fields: [],
  },
];

const inputCls = "w-full bg-white border border-zinc-200 rounded-md px-4 py-2.5 focus:ring-2 focus:ring-[#0033FF]/20 focus:border-[#0033FF] outline-none transition-all";

export default function Integrations() {
  const [saved, setSaved] = useState({});
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/integrations").then((r) => {
    const map = {};
    r.data.forEach((i) => { map[i.provider] = i; });
    setSaved(map);
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const openConfig = (p) => { setActive(p); setForm(saved[p.id]?.config || {}); };

  const connect = async () => {
    setBusy(true);
    try {
      await api.post("/integrations", { provider: active.id, config: form, connected: true });
      toast.success(`${active.name} ansluten`);
      setActive(null);
      load();
    } catch { toast.error("Kunde inte spara integrationen"); }
    finally { setBusy(false); }
  };

  const disconnect = async (id) => {
    await api.delete(`/integrations/${id}`);
    toast.success("Frånkopplad");
    load();
  };

  return (
    <div className="p-6 sm:p-8">
      <div>
        <h1 className="font-display font-bold text-3xl tracking-tight">Integrationer</h1>
        <p className="mt-1 text-zinc-500">Anslut din helpdesk, telefoni och AI-leverantörer.</p>
      </div>

      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {PROVIDERS.map((p) => {
          const isConnected = p.connectedByDefault || saved[p.id]?.connected;
          return (
            <div key={p.id} data-testid={`integration-card-${p.id}`} className="bg-white border border-zinc-200 rounded-lg p-6 flex flex-col">
              <div className="flex items-start justify-between">
                <div className="h-12 w-12 rounded-lg bg-zinc-50 border border-zinc-200 flex items-center justify-center">{p.icon}</div>
                {isConnected ? (
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-2.5 py-1 rounded-full">
                    <CheckCircle size={14} weight="fill" /> Ansluten
                  </span>
                ) : (
                  <span className="text-xs font-semibold text-zinc-400 bg-zinc-100 px-2.5 py-1 rounded-full">Inte ansluten</span>
                )}
              </div>
              <div className="mt-4 flex items-center gap-2">
                <h3 className="font-display font-bold text-lg">{p.name}</h3>
                <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500">{p.category}</span>
              </div>
              <p className="mt-1.5 text-sm text-zinc-500 flex-1">{p.desc}</p>
              {!p.connectedByDefault && (
                <div className="mt-5 flex items-center gap-2">
                  <button data-testid={`integration-connect-${p.id}`} onClick={() => openConfig(p)}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-[#0033FF] hover:bg-[#002BE6] text-white rounded-md px-4 py-2 font-semibold text-sm transition-colors">
                    <Plugs size={16} weight="bold" /> {isConnected ? "Hantera" : "Anslut"}
                  </button>
                  {isConnected && (
                    <button data-testid={`integration-disconnect-${p.id}`} onClick={() => disconnect(p.id)}
                      className="px-3 py-2 rounded-md border border-zinc-200 text-sm font-semibold text-zinc-600 hover:bg-zinc-50">Ta bort</button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">{active?.icon} Anslut {active?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {active?.fields.map((f) => (
              <div key={f.key}>
                <label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">{f.label}</label>
                <input data-testid={`integration-field-${f.key}`} type={f.secret ? "password" : "text"} placeholder={f.placeholder}
                  value={form[f.key] || ""} onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                  className={`mt-1.5 ${inputCls}`} />
              </div>
            ))}
            <p className="text-xs text-zinc-400">Uppgifterna lagras säkert. Live-routing av ärenden och samtal aktiveras när de verifierats.</p>
          </div>
          <DialogFooter>
            <button data-testid="integration-save-button" onClick={connect} disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#0033FF] hover:bg-[#002BE6] text-white rounded-md px-5 py-2.5 font-semibold transition-colors disabled:opacity-60">
              {busy ? <CircleNotch size={18} className="animate-spin" /> : <CheckCircle size={18} weight="bold" />} Spara anslutning
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
